/**
 * Pure helpers behind the AccessRulesEditor. Access rules decide *who can see
 * an app* — a security-relevant surface — so the normalization and mode logic
 * lives here, separately testable from the React component.
 */

export type AccessRuleItem = { type: string; value: string | null };

export type AccessMode = "all" | "email";

/** Wire format expected by the admin API: `value` is omitted when null. */
export function serializeRules(rules: AccessRuleItem[]): { type: string; value?: string }[] {
  return rules.map(({ type, value }) => (value != null ? { type, value } : { type }));
}

/** Email rules win: any `email` rule means the app is restricted to a list. */
export function deriveMode(rules: AccessRuleItem[]): AccessMode {
  return rules.some((r) => r.type === "email") ? "email" : "all";
}

/**
 * Rule set when the admin switches mode. "email" yields an empty list — which
 * means the app is visible to *no one* until addresses are added (intentional;
 * surfaced to the admin via `admin.access.noEmails`).
 */
export function rulesForMode(mode: AccessMode): AccessRuleItem[] {
  return mode === "all" ? [{ type: "all-authenticated", value: null }] : [];
}

export function emailRulesFrom(rules: AccessRuleItem[]): AccessRuleItem[] {
  return rules.filter((r) => r.type === "email");
}

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Append a normalized email rule. Returns `null` when the input is rejected
 * (empty after trimming, or a case-insensitive duplicate) so the caller can
 * leave the input field untouched.
 */
export function addEmailRule(
  existing: AccessRuleItem[],
  rawInput: string,
): AccessRuleItem[] | null {
  const email = normalizeEmail(rawInput);
  if (!email || existing.some((r) => r.value === email)) return null;
  return [...existing, { type: "email", value: email }];
}

export function removeEmailRule(existing: AccessRuleItem[], email: string): AccessRuleItem[] {
  return existing.filter((r) => r.value !== email);
}
